import type { BudgetDocument, BudgetSnapshot } from '../types'
import { deepClone } from '../utils'

/**
 * Undo/redo stack of document snapshots.
 * Does not persist — serializer is responsible for long-term storage.
 */
export class BudgetHistory {
  private readonly undoStack: BudgetSnapshot[] = []
  private readonly redoStack: BudgetSnapshot[] = []
  private readonly limit: number

  constructor(limit = 50) {
    this.limit = Math.max(1, limit)
  }

  push(document: BudgetDocument): void {
    this.undoStack.push(toSnapshot(document))
    if (this.undoStack.length > this.limit) {
      this.undoStack.shift()
    }
    this.redoStack.length = 0
  }

  canUndo(): boolean {
    return this.undoStack.length > 0
  }

  canRedo(): boolean {
    return this.redoStack.length > 0
  }

  undo(current: BudgetDocument): BudgetDocument | null {
    const previous = this.undoStack.pop()
    if (!previous) return null
    this.redoStack.push(toSnapshot(current))
    return deepClone(previous.document)
  }

  redo(current: BudgetDocument): BudgetDocument | null {
    const next = this.redoStack.pop()
    if (!next) return null
    this.undoStack.push(toSnapshot(current))
    return deepClone(next.document)
  }

  clear(): void {
    this.undoStack.length = 0
    this.redoStack.length = 0
  }

  size(): { undo: number; redo: number } {
    return { undo: this.undoStack.length, redo: this.redoStack.length }
  }
}

function toSnapshot(document: BudgetDocument): BudgetSnapshot {
  return {
    schemaVersion: 1,
    savedAt: new Date().toISOString(),
    document: deepClone(document),
  }
}
